'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import type { DefaultTheme } from 'styled-components';

export type AlertTone = 'info' | 'success' | 'warn' | 'error';

export interface AlertProps {
  /** Tone — controls icon color, accent stripe, and ARIA role. */
  tone?: AlertTone;
  /** Optional leading icon (rendered in the tone color). */
  icon?: React.ReactNode;
  /** Bold first line. */
  title: React.ReactNode;
  /** Soft second line. */
  body?: React.ReactNode;
  /** Optional trailing action (button, link). */
  action?: React.ReactNode;
  /** When provided, renders a dismiss "×" button that calls this on click. */
  onDismiss?: () => void;
  className?: string;
}

const toneColor = (tone: AlertTone, theme: DefaultTheme) => {
  const map = {
    info: theme.colors.semantic.info,
    success: theme.colors.semantic.success,
    warn: theme.colors.semantic.warn,
    error: theme.colors.semantic.error,
  } as const;
  return map[tone];
};

const Surface = styled.div<{ $tone: AlertTone }>`
  display: flex;
  align-items: flex-start;
  gap: ${(p) => p.theme.space[3]};
  padding: ${(p) => p.theme.space[3]} ${(p) => p.theme.space[4]};
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.lg};
  ${(p) => css`
    border-left: 3px solid ${toneColor(p.$tone, p.theme).color};
  `}
`;

const IconSlot = styled.span<{ $tone: AlertTone }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: ${(p) => toneColor(p.$tone, p.theme).color};
`;

const TextStack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Title = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  color: ${(p) => p.theme.colors.neutral.ink};
  line-height: 1.4;
`;

const Body = styled.div`
  margin-top: ${(p) => p.theme.space[1]};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  line-height: 1.55;
`;

const ActionSlot = styled.div`
  flex-shrink: 0;
  align-self: center;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  align-self: flex-start;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.colors.neutral.inkMute};
  border-radius: ${(p) => p.theme.radius.sm};
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(p) => p.theme.colors.neutral.panelAlt};
    color: ${(p) => p.theme.colors.neutral.ink};
  }
  ${focusRing}
`;

/**
 * Inline alert: tone-coloured left stripe, icon, title, body, optional
 * action and dismiss. Use within a content region (drawer, panel,
 * dashboard widget). For page-level full-bleed treatment use {@link
 * Banner}; for transient floating alerts use Toast (separate primitive).
 */
export function Alert({
  tone = 'info',
  icon,
  title,
  body,
  action,
  onDismiss,
  className,
}: AlertProps) {
  const role = tone === 'error' ? 'alert' : 'status';
  return (
    <Surface role={role} $tone={tone} className={className}>
      {icon !== undefined && <IconSlot $tone={tone}>{icon}</IconSlot>}
      <TextStack>
        <Title>{title}</Title>
        {body !== undefined && <Body>{body}</Body>}
      </TextStack>
      {action !== undefined && <ActionSlot>{action}</ActionSlot>}
      {onDismiss !== undefined && (
        <DismissButton type="button" onClick={onDismiss} aria-label="Dismiss">
          ×
        </DismissButton>
      )}
    </Surface>
  );
}
